import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Panel } from "@/components/Layout";
import { Home, Truck, Sun, Plus, Check } from "lucide-react";
import { toast } from "sonner";

const FLEETS = [
  { key: "real_estate", label: "Real Estate", icon: Home },
  { key: "logistics", label: "Logistics", icon: Truck },
  { key: "solar", label: "Solar", icon: Sun },
];

export default function ReferralBridge() {
  const [referrals, setReferrals] = useState([]);
  const [form, setForm] = useState({
    fleet: "real_estate", referrer_name: "", client_name: "", client_contact: "", interest: "home", note: ""
  });

  const load = async () => {
    const { data } = await client.get("/referrals");
    setReferrals(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.referrer_name || !form.client_name) return toast.error("Referrer and client required");
    try {
      await client.post("/referrals", form);
      toast.success("Signal received on the Bridge");
      setForm({ ...form, referrer_name: "", client_name: "", client_contact: "", note: "" });
      load();
    } catch {
      toast.error("Bridge signal failed");
    }
  };

  const accept = async (id) => {
    await client.post(`/referrals/${id}/accept`);
    toast.success("Referral accepted into the queue");
    load();
  };

  return (
    <div className="space-y-6" data-testid="bridge-page">
      <header>
        <div className="label-tag mb-2">/// MODULE 04 — CROSS-FLEET SIGNAL ROUTER</div>
        <h1 className="heading-display text-4xl sm:text-5xl">Cross-Fleet Referral Bridge</h1>
        <p className="text-[#888] text-sm mt-2 max-w-2xl">
          Direct signal input from Real Estate, Logistics, and Solar vessels routed into the Protection hull for engagement.
        </p>
      </header>

      <div className="hud-panel overflow-hidden" data-testid="bridge-marquee">
        <div className="hud-corner-tl" /><div className="hud-corner-tr" />
        <div className="hud-corner-bl" /><div className="hud-corner-br" />
        <div className="py-4 relative">
          <div className="marquee-track">
            {[...referrals, ...referrals].map((r, i) => (
              <div key={`${r.id}-${i}`} className="shrink-0 flex items-center gap-3 px-4 border-r border-[#332A0C]">
                <span className="pulse-dot" />
                <span className="label-tag text-gold">{r.fleet.replace("_", " ")}</span>
                <span className="text-sm font-bold">{r.client_name}</span>
                <span className="text-[#666] text-xs">via {r.referrer_name}</span>
              </div>
            ))}
            {referrals.length === 0 && <div className="px-4 text-[#666]">No incoming signals yet — submit one to begin.</div>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="fleet-counters">
        {FLEETS.map((f) => {
          const Icon = f.icon;
          const count = referrals.filter((r) => r.fleet === f.key).length;
          return (
            <div key={f.key} className="hud-panel p-5" data-testid={`fleet-${f.key}`}>
              <div className="hud-corner-tl" /><div className="hud-corner-tr" />
              <div className="hud-corner-bl" /><div className="hud-corner-br" />
              <div className="relative z-10 flex items-center justify-between">
                <div>
                  <div className="label-tag">{f.label} Vessel</div>
                  <div className="metric-value text-4xl mt-1">{count}</div>
                  <div className="label-tag text-[#666] mt-1">Signals received</div>
                </div>
                <Icon className="w-8 h-8 text-gold" />
              </div>
            </div>
          );
        })}
      </div>

      <Panel tag="// SUBMIT" title="Manual Signal Injection" testId="bridge-form-panel">
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="bridge-form">
          <Field label="Fleet">
            <select value={form.fleet} onChange={(e) => setForm({ ...form, fleet: e.target.value })} className="hud-input" data-testid="bridge-fleet">
              {FLEETS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </Field>
          <Field label="Interest Sector">
            <select value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })} className="hud-input" data-testid="bridge-interest">
              <option value="life">Life</option>
              <option value="auto">Auto</option>
              <option value="home">Home</option>
              <option value="commercial">Commercial</option>
            </select>
          </Field>
          <Field label="Referrer Vessel/Agent">
            <input required value={form.referrer_name} onChange={(e) => setForm({ ...form, referrer_name: e.target.value })} className="hud-input" data-testid="bridge-referrer" />
          </Field>
          <Field label="Client Name">
            <input required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="hud-input" data-testid="bridge-client" />
          </Field>
          <Field label="Client Contact">
            <input value={form.client_contact} onChange={(e) => setForm({ ...form, client_contact: e.target.value })} className="hud-input" data-testid="bridge-contact" />
          </Field>
          <Field label="Note">
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="hud-input" data-testid="bridge-note" />
          </Field>
          <div className="md:col-span-2 flex justify-end">
            <button type="submit" className="btn-gold inline-flex items-center gap-2" data-testid="bridge-submit">
              <Plus className="w-4 h-4" /> Transmit Signal
            </button>
          </div>
        </form>
      </Panel>

      <Panel tag={`// QUEUE — ${referrals.length} SIGNALS`} title="Signal Queue" testId="bridge-queue">
        <div className="space-y-2">
          {referrals.map((r) => {
            const F = FLEETS.find((f) => f.key === r.fleet);
            const Icon = F?.icon || Home;
            return (
              <div key={r.id} className="grid grid-cols-12 items-center gap-3 p-3 border border-[#1a1a1a] hover:border-gold transition-colors" data-testid={`referral-row-${r.id}`}>
                <div className="col-span-12 md:col-span-2 flex items-center gap-2">
                  <Icon className="w-4 h-4 text-gold" />
                  <span className="label-tag">{F?.label}</span>
                </div>
                <div className="col-span-12 md:col-span-3">
                  <div className="font-bold">{r.client_name}</div>
                  <div className="label-tag text-[#888]">{r.client_contact || "—"}</div>
                </div>
                <div className="col-span-6 md:col-span-3">
                  <div className="text-sm text-[#aaa]">via {r.referrer_name}</div>
                  <div className="text-xs text-[#666]">{r.note}</div>
                </div>
                <div className="col-span-3 md:col-span-2">
                  <span className="label-tag text-gold border border-gold px-2 py-1">{r.interest}</span>
                </div>
                <div className="col-span-3 md:col-span-2 flex justify-end">
                  {r.status === "incoming" ? (
                    <button onClick={() => accept(r.id)} className="btn-ghost-gold inline-flex items-center gap-1" data-testid={`accept-ref-${r.id}`}>
                      <Check className="w-3 h-3" /> Accept
                    </button>
                  ) : (
                    <span className="label-tag text-[#34C759]">{r.status}</span>
                  )}
                </div>
              </div>
            );
          })}
          {referrals.length === 0 && <div className="text-center text-[#666] py-10">No signals in queue.</div>}
        </div>
      </Panel>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-tag block mb-1">{label}</label>
      {children}
    </div>
  );
}
